import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { syncDateRange } from '../utils/dateUtils';

const EVENT_TYPES = [
  { value: 'FESTIVAL', label: 'Festival / Religious' },
  { value: 'CONFERENCE', label: 'Conference / Convention' },
  { value: 'SPORTS', label: 'Sports Event' },
  { value: 'CONCERT', label: 'Concert / Entertainment' },
  { value: 'HOLIDAY', label: 'Public Holiday' },
  { value: 'EXHIBITION', label: 'Exhibition / Trade Show' },
  { value: 'WEDDING_SEASON', label: 'Wedding Season' },
  { value: 'GOVERNMENT', label: 'Government / Political' },
  { value: 'OTHER', label: 'Other' }
];

const IMPACT_OPTIONS = [
  { value: 'HIGH', label: 'High', color: '#dc2626', bg: '#fef2f2' },
  { value: 'MEDIUM', label: 'Medium', color: '#d97706', bg: '#fffbeb' },
  { value: 'LOW', label: 'Low', color: '#16a34a', bg: '#f0fdf4' }
];

const EVENT_COLORS = {
  FESTIVAL: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  CONFERENCE: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  SPORTS: { bg: '#d1fae5', border: '#10b981', text: '#065f46' },
  CONCERT: { bg: '#ede9fe', border: '#8b5cf6', text: '#5b21b6' },
  HOLIDAY: { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  EXHIBITION: { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' },
  WEDDING_SEASON: { bg: '#fff1f2', border: '#f43f5e', text: '#be123c' },
  GOVERNMENT: { bg: '#f1f5f9', border: '#64748b', text: '#334155' },
  OTHER: { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' }
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const EMPTY_FORM = { event_name: '', event_type: 'OTHER', start_date: '', end_date: '', expected_impact: 'MEDIUM', notes: '', is_recurring: false };

function PmsLocalEventCalendar({ token, selectedProperty }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formLoading, setFormLoading] = useState(false);
  const [viewMode, setViewMode] = useState('calendar'); // 'table' or 'calendar'
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  // Global events browser
  const [showGlobal, setShowGlobal] = useState(false);
  const [globalEvents, setGlobalEvents] = useState([]);
  const [globalCities, setGlobalCities] = useState([]);
  const [globalCity, setGlobalCity] = useState('');
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalSelected, setGlobalSelected] = useState(new Set());
  const [globalSyncing, setGlobalSyncing] = useState(false);
  const [insightsData, setInsightsData] = useState(null); // { events, summary }
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [expandedInsight, setExpandedInsight] = useState(null); // event external_event_id
  const [showInsights, setShowInsights] = useState(false);

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const loadEvents = useCallback(async () => {
    if (!selectedProperty?.id) { setEvents([]); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/pms/local-events', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, headers]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Global events functions
  const [propertyCity, setPropertyCity] = useState('');
  const [cityRestricted, setCityRestricted] = useState(false);

  const loadGlobalEvents = async (city) => {
    setGlobalLoading(true); setGlobalSelected(new Set());
    try {
      const q = new URLSearchParams();
      if (city) q.set('city', city);
      q.set('from', new Date().toISOString().slice(0, 10));
      const res = await fetch(`/api/v1/event-calendar/global?${q.toString()}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setGlobalEvents(data.events || []);
        setGlobalCities(data.available_cities || []);
        if (data.property_city) { setPropertyCity(data.property_city); setGlobalCity(data.property_city); }
        setCityRestricted(Boolean(data.city_restricted));
      }
    } catch {}
    finally { setGlobalLoading(false); }
  };

  const toggleGlobalSelect = (id) => {
    setGlobalSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const syncGlobalToProperty = async () => {
    if (!selectedProperty?.id) return setError('Select a property first');
    if (!globalSelected.size) return setError('Select at least one event');
    setGlobalSyncing(true); setError(''); setMessage('');
    try {
      const res = await fetch('/api/v1/event-calendar/global/sync-to-property', {
        method: 'POST', headers: headers(true),
        body: JSON.stringify({ event_ids: Array.from(globalSelected) }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Sync failed');
      setMessage(`Pulled ${data.inserted} new events to your property (${data.skipped} already existed)`);
      setGlobalSelected(new Set());
      loadEvents(); // refresh local events
    } catch (err) { setError(err.message); }
    finally { setGlobalSyncing(false); }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const q = new URLSearchParams();
      if (propertyCity) q.set('city', propertyCity);
      else if (globalCity) q.set('city', globalCity);
      q.set('from', new Date().toISOString().slice(0, 10));
      const res = await fetch(`/api/v1/event-calendar/insights?${q.toString()}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setInsightsData(data);
        setShowInsights(true);
      }
    } catch {}
    finally { setInsightsLoading(false); }
  };

  // Find PredictHQ insight for a global event by external_event_id
  const getInsightForEvent = (externalEventId) => {
    if (!insightsData?.events || !externalEventId) return null;
    return insightsData.events.find(e => e.external_event_id === externalEventId) || null;
  };

  const openAdd = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (ev) => {
    setEditId(ev.event_id);
    setForm({
      event_name: ev.event_name || '',
      event_type: ev.event_type || 'OTHER',
      start_date: ev.start_date || '',
      end_date: ev.end_date || '',
      expected_impact: ev.expected_impact || 'MEDIUM',
      notes: ev.notes || '',
      is_recurring: ev.is_recurring || false
    });
    setShowForm(true);
  };

  const saveEvent = async () => {
    if (!form.event_name.trim()) { setError('Event name is required'); return; }
    if (!form.start_date || !form.end_date) { setError('Start and end dates are required'); return; }
    if (form.end_date < form.start_date) { setError('End date must be on or after start date'); return; }
    setFormLoading(true);
    setError('');
    try {
      const url = editId ? `/api/v1/pms/local-events/${editId}` : '/api/v1/pms/local-events';
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(true), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save');
      setMessage(editId ? 'Event updated successfully' : 'Event created successfully');
      setShowForm(false);
      setEditId(null);
      await loadEvents();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setFormLoading(false);
    }
  };

  const deleteEvent = async (id, name) => {
    if (!window.confirm(`Delete event "${name}"?`)) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/pms/local-events/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to delete');
      setMessage('Event deleted');
      await loadEvents();
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    }
  };

  const inputStyle = { width: '100%', padding: '0.4rem 0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' };
  const thStyle = { padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.82rem' };
  const tdStyle = { padding: '0.45rem 0.6rem', fontSize: '0.83rem' };

  const today = new Date().toISOString().split('T')[0];
  const upcomingEvents = events.filter(e => e.end_date >= today);
  const pastEvents = events.filter(e => e.end_date < today);

  const impactBadge = (impact) => {
    const opt = IMPACT_OPTIONS.find(o => o.value === impact) || IMPACT_OPTIONS[1];
    return (
      <span style={{ fontSize: '0.72rem', padding: '1px 8px', borderRadius: 4, fontWeight: 600, background: opt.bg, color: opt.color }}>
        {opt.label}
      </span>
    );
  };

  const typeBadge = (type) => {
    const label = EVENT_TYPES.find(t => t.value === type)?.label || type;
    const colors = EVENT_COLORS[type] || EVENT_COLORS.OTHER;
    return (
      <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, fontWeight: 600, background: colors.bg, color: colors.text }}>
        {label}
      </span>
    );
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // ── Calendar logic ──
  const calendarDays = useMemo(() => {
    const firstDay = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const startPad = firstDay.getDay();
    const totalDays = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(d);
    return cells;
  }, [calYear, calMonth]);

  const eventsForDate = useCallback((day) => {
    if (!day) return [];
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(ev => ev.start_date <= dateStr && ev.end_date >= dateStr);
  }, [events, calYear, calMonth]);

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };
  const goToday = () => { setCalMonth(new Date().getMonth()); setCalYear(new Date().getFullYear()); };

  const todayDay = new Date().getDate();
  const todayMonth = new Date().getMonth();
  const todayYear = new Date().getFullYear();

  // ── Calendar View ──
  const renderCalendar = () => (
    <div className="card card-body" style={{ marginBottom: '0.8rem' }}>
      {/* Month nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
        <button onClick={prevMonth}
          style={{ padding: '0.3rem 0.7rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700 }}>
          &lt;
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--theme-primary)' }}>{MONTHS[calMonth]} {calYear}</span>
          <button onClick={goToday}
            style={{ padding: '2px 10px', fontSize: '0.75rem', background: '#e2e8f0', color: '#334155', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
            Today
          </button>
        </div>
        <button onClick={nextMonth}
          style={{ padding: '0.3rem 0.7rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700 }}>
          &gt;
        </button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.85rem', padding: '0.45rem 0', color: d === 'Sun' ? '#dc2626' : 'var(--theme-text-secondary)', background: '#f1f5f9', borderRadius: 4 }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginTop: 2 }}>
        {calendarDays.map((day, idx) => {
          const dayEvents = eventsForDate(day);
          const isToday = day === todayDay && calMonth === todayMonth && calYear === todayYear;
          const isSunday = idx % 7 === 0;
          return (
            <div key={idx}
              style={{
                minHeight: 110,
                border: isToday ? '2px solid var(--theme-primary)' : '1px solid var(--theme-border)',
                borderRadius: 6,
                padding: '4px 6px',
                background: isToday ? '#eff6ff' : day ? '#fff' : '#f8fafc',
                position: 'relative'
              }}>
              {day && (
                <>
                  <div style={{
                    fontSize: '0.88rem',
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? 'var(--theme-primary)' : isSunday ? '#dc2626' : '#475569',
                    marginBottom: 4,
                    ...(isToday ? { background: 'var(--theme-primary)', color: '#fff', width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem' } : {})
                  }}>
                    {day}
                  </div>
                  {dayEvents.map(ev => {
                    const colors = EVENT_COLORS[ev.event_type] || EVENT_COLORS.OTHER;
                    const impactDot = ev.expected_impact === 'HIGH' ? '#dc2626' : ev.expected_impact === 'MEDIUM' ? '#d97706' : '#16a34a';
                    return (
                      <div key={ev.event_id}
                        onClick={() => openEdit(ev)}
                        title={`${ev.event_name}\n${formatDate(ev.start_date)} - ${formatDate(ev.end_date)}\nImpact: ${ev.expected_impact}${ev.notes ? '\n' + ev.notes : ''}`}
                        style={{
                          fontSize: '0.78rem',
                          lineHeight: '1.3',
                          padding: '3px 6px',
                          marginBottom: 3,
                          borderRadius: 4,
                          background: colors.bg,
                          borderLeft: `4px solid ${colors.border}`,
                          color: colors.text,
                          fontWeight: 600,
                          cursor: 'pointer',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5
                        }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: impactDot, flexShrink: 0, display: 'inline-block' }} />
                        {ev.event_name}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.6rem', paddingTop: '0.5rem', borderTop: '1px solid var(--theme-border)' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--theme-text-secondary)' }}>Impact:</span>
        {IMPACT_OPTIONS.map(o => (
          <span key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.color, display: 'inline-block' }} />
            {o.label}
          </span>
        ))}
        <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginLeft: 'auto' }}>Click an event to edit</span>
      </div>
    </div>
  );

  const renderTable = (rows, label, showEmpty) => (
    <div className="card card-body" style={{ marginBottom: '0.8rem' }}>
      <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.45rem', fontSize: '0.95rem' }}>{label} ({rows.length})</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#334155', color: '#fff' }}>
              <th style={thStyle}>Event Name</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Start Date</th>
              <th style={thStyle}>End Date</th>
              <th style={thStyle}>Impact</th>
              <th style={thStyle}>Recurring</th>
              <th style={thStyle}>Notes</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(ev => (
              <tr key={ev.event_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{ev.event_name}</td>
                <td style={tdStyle}>{typeBadge(ev.event_type)}</td>
                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDate(ev.start_date)}</td>
                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDate(ev.end_date)}</td>
                <td style={tdStyle}>{impactBadge(ev.expected_impact)}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{ev.is_recurring ? 'Yes' : '-'}</td>
                <td style={{ ...tdStyle, color: 'var(--theme-text-secondary)', maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3' }}>{ev.notes || '-'}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: '0.3rem' }}>
                    <button onClick={() => openEdit(ev)}
                      style={{ fontSize: '0.75rem', padding: '2px 8px', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => deleteEvent(ev.event_id, ev.event_name)}
                      style={{ fontSize: '0.75rem', padding: '2px 8px', background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {showEmpty && !rows.length && (
              <tr><td colSpan={8} style={{ padding: '0.7rem', color: 'var(--theme-text-muted)', textAlign: 'center' }}>No events</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const viewBtn = (mode, label) => ({
    padding: '0.3rem 0.7rem',
    background: viewMode === mode ? 'var(--theme-primary)' : '#fff',
    color: viewMode === mode ? '#fff' : '#1f2937',
    border: '1px solid #cbd5e0',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.82rem'
  });

  return (
    <div style={{ padding: '0.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.6rem' }}>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--theme-primary)', fontSize: '1rem' }}>Local Event Calendar</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>
            Add local events (festivals, conferences, holidays) that affect hotel demand. This data is used by AI Occupancy Forecasting.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <button onClick={() => setViewMode('calendar')} style={viewBtn('calendar')}>Calendar</button>
          <button onClick={() => setViewMode('table')} style={viewBtn('table')}>Table</button>
          <button onClick={openAdd}
            style={{ padding: '0.35rem 0.8rem', background: '#166534', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
            + Add Event
          </button>
          <button onClick={() => { setShowGlobal(!showGlobal); if (!showGlobal && !globalEvents.length) loadGlobalEvents(''); }}
            style={{ padding: '0.35rem 0.8rem', background: showGlobal ? '#7c3aed' : '#1e40af', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
            {showGlobal ? 'Hide Global Events' : 'Browse Global Events'}
          </button>
          <button onClick={loadEvents}
            style={{ padding: '0.35rem 0.7rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem' }}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{error}</div>}
      {message && <div style={{ background: '#f0fdf4', color: '#166534', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{message}</div>}
      {loading && <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Loading...</div>}

      {/* Global Events Browser Panel */}
      {showGlobal && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.4rem' }}>
            <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.92rem' }}>Global Events — Pull to Your Property</div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button onClick={() => { if (!insightsData) loadInsights(); setShowInsights(!showInsights); }} disabled={insightsLoading}
                style={{ padding: '0.35rem 0.8rem', border: 'none', borderRadius: 6, cursor: insightsLoading ? 'wait' : 'pointer',
                  fontWeight: 700, background: showInsights ? '#7c3aed' : '#6d28d9', color: '#fff', fontSize: '0.82rem' }}>
                {insightsLoading ? 'Loading...' : showInsights ? 'Hide AI Predictions' : 'View AI Predictions'}
              </button>
              {globalSelected.size > 0 && (
                <button onClick={syncGlobalToProperty} disabled={globalSyncing}
                  style={{ padding: '0.35rem 1rem', border: 'none', borderRadius: 6, cursor: globalSyncing ? 'wait' : 'pointer',
                    fontWeight: 700, background: '#059669', color: '#fff', fontSize: '0.82rem' }}>
                  {globalSyncing ? 'Pulling...' : `Pull ${globalSelected.size} Event(s) to My Property`}
                </button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.5rem', alignItems: 'center' }}>
            {cityRestricted ? (
              <span style={{ padding: '0.35rem 0.6rem', background: '#dbeafe', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, color: '#1e40af', border: '1px solid #93c5fd' }}>
                {propertyCity}
              </span>
            ) : (
              <select value={globalCity} onChange={e => { setGlobalCity(e.target.value); loadGlobalEvents(e.target.value); }}
                style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.82rem' }}>
                <option value="">All Cities</option>
                {globalCities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <button onClick={() => loadGlobalEvents(globalCity)} disabled={globalLoading}
              style={{ padding: '0.35rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', background: '#fff', fontSize: '0.82rem', fontWeight: 600 }}>
              {globalLoading ? '...' : 'Refresh'}
            </button>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 'auto' }}>
              {globalEvents.length} events available{globalCity ? ` in ${globalCity}` : ''}
              {globalSelected.size > 0 && ` · ${globalSelected.size} selected`}
            </span>
          </div>
          {globalEvents.length > 0 ? (
            <div style={{ maxHeight: '300px', overflowY: 'auto', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: '#1e40af', color: '#fff' }}>
                    <th style={{ padding: '0.4rem', width: 28 }}>
                      <input type="checkbox"
                        checked={globalSelected.size === globalEvents.length && globalEvents.length > 0}
                        onChange={() => setGlobalSelected(prev => prev.size === globalEvents.length ? new Set() : new Set(globalEvents.map(e => e.id)))}
                        style={{ cursor: 'pointer' }} />
                    </th>
                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Event</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Type</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '0.4rem', textAlign: 'center' }}>Impact</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>City</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {globalEvents.map((evt, i) => (
                    <tr key={evt.id} style={{ borderBottom: '1px solid #f3f4f6', background: globalSelected.has(evt.id) ? '#eff6ff' : (i % 2 === 0 ? '#fff' : '#fafafa'), cursor: 'pointer' }}
                      onClick={() => toggleGlobalSelect(evt.id)}>
                      <td style={{ padding: '0.35rem 0.4rem', textAlign: 'center' }}>
                        <input type="checkbox" checked={globalSelected.has(evt.id)} onChange={() => {}} style={{ cursor: 'pointer' }} />
                      </td>
                      <td style={{ padding: '0.35rem 0.4rem', fontWeight: 600 }}>{evt.event_name}</td>
                      <td style={{ padding: '0.35rem 0.4rem' }}>
                        <span style={{ fontSize: '0.7rem', padding: '1px 5px', borderRadius: 3, fontWeight: 600,
                          background: EVENT_COLORS[evt.event_type]?.bg || '#f3f4f6',
                          color: EVENT_COLORS[evt.event_type]?.text || '#475569' }}>
                          {evt.event_type}
                        </span>
                      </td>
                      <td style={{ padding: '0.35rem 0.4rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {evt.start_date}{evt.end_date && evt.end_date !== evt.start_date ? ` — ${evt.end_date}` : ''}
                      </td>
                      <td style={{ padding: '0.35rem 0.4rem', textAlign: 'center', fontWeight: 700, fontSize: '0.75rem',
                        color: evt.expected_impact === 'HIGH' ? '#dc2626' : evt.expected_impact === 'MEDIUM' ? '#d97706' : '#6b7280' }}>
                        {evt.expected_impact}
                      </td>
                      <td style={{ padding: '0.35rem 0.4rem', fontSize: '0.78rem', color: '#6b7280' }}>{evt.city_name}</td>
                      <td style={{ padding: '0.35rem 0.4rem', fontSize: '0.7rem', color: '#94a3b8' }}>{evt.vendor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', background: '#fff', borderRadius: 6, fontSize: '0.85rem' }}>
              {globalLoading ? 'Loading global events...' : 'No global events found. Ask your admin to configure and sync Event Calendar APIs in Settings.'}
            </div>
          )}
          {/* AI Predictions Panel */}
          {showInsights && insightsData && (
            <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#faf5ff', borderRadius: 8, border: '1px solid #d8b4fe' }}>
              <div style={{ fontWeight: 700, color: '#7c3aed', fontSize: '0.92rem', marginBottom: '0.5rem' }}>
                AI Event Predictions
              </div>

              {/* Summary Cards */}
              {insightsData.summary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '0.4rem', marginBottom: '0.6rem' }}>
                  {[
                    { label: 'Events', value: insightsData.summary.total_events, color: '#7c3aed' },
                    { label: 'High Impact', value: insightsData.summary.high_impact_events, color: '#dc2626' },
                    { label: 'Attendance', value: (insightsData.summary.total_predicted_attendance || 0).toLocaleString(), color: '#059669' },
                    { label: 'Hotel Spend', value: `$${(insightsData.summary.total_accommodation_spend_usd || 0).toLocaleString()}`, color: '#1e40af' },
                    { label: 'F&B Spend', value: `$${(insightsData.summary.total_hospitality_spend_usd || 0).toLocaleString()}`, color: '#9333ea' },
                  ].map((c, i) => (
                    <div key={i} style={{ padding: '0.4rem', background: '#fff', borderRadius: 6, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: 600 }}>{c.label}</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 800, color: c.color }}>{c.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Insights per event */}
              {insightsData.events?.length > 0 && (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {insightsData.events.map(evt => {
                    const rank = evt.phq_rank || 0;
                    const isExpanded = expandedInsight === evt.external_event_id;
                    return (
                      <div key={evt.id} style={{ marginBottom: '0.4rem', background: '#fff', borderRadius: 6, border: `1.5px solid ${rank >= 70 ? '#fca5a5' : rank >= 40 ? '#fcd34d' : '#e5e7eb'}`, overflow: 'hidden' }}>
                        <div onClick={() => setExpandedInsight(isExpanded ? null : evt.external_event_id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.6rem', cursor: 'pointer' }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.82rem', color: '#fff', flexShrink: 0,
                            background: rank >= 70 ? '#dc2626' : rank >= 40 ? '#d97706' : '#6b7280' }}>
                            {rank}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{evt.title}</div>
                            <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                              {evt.category} · {evt.start_date}{evt.end_date && evt.end_date !== evt.start_date ? ` — ${evt.end_date}` : ''}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            {evt.phq_attendance ? <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#059669' }}>{Number(evt.phq_attendance).toLocaleString()} pax</div> : null}
                            {evt.predicted_spend_accommodation ? <div style={{ fontSize: '0.72rem', color: '#1e40af', fontWeight: 600 }}>${Number(evt.predicted_spend_accommodation).toLocaleString()} hotel</div> : null}
                          </div>
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{isExpanded ? '▲' : '▼'}</span>
                        </div>

                        {isExpanded && (
                          <div style={{ padding: '0.6rem', borderTop: '1px solid #f3f4f6', background: '#faf5ff' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem', fontSize: '0.78rem' }}>
                              {/* Spend */}
                              <div style={{ background: '#fff', padding: '0.5rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Predicted Spend (USD)</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total:</span><b>${Number(evt.predicted_spend_total || 0).toLocaleString()}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Accommodation:</span><b style={{ color: '#1e40af' }}>${Number(evt.predicted_spend_accommodation || 0).toLocaleString()}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>F&B / Hospitality:</span><b style={{ color: '#9333ea' }}>${Number(evt.predicted_spend_hospitality || 0).toLocaleString()}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Transport:</span><b>${Number(evt.predicted_spend_transportation || 0).toLocaleString()}</b></div>
                              </div>

                              {/* Ranking */}
                              <div style={{ background: '#fff', padding: '0.5rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: '0.25rem', fontSize: '0.75rem' }}>AI Rankings</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Event Impact Rank:</span><b style={{ color: rank >= 70 ? '#dc2626' : rank >= 40 ? '#d97706' : '#6b7280' }}>{rank}/100</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Local Rank:</span><b>{evt.local_rank || '-'}/100</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Attendance:</span><b style={{ color: '#059669' }}>{evt.phq_attendance ? Number(evt.phq_attendance).toLocaleString() : '-'}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>State:</span><span>{evt.event_state || 'active'}</span></div>
                              </div>

                              {/* Location */}
                              <div style={{ background: '#fff', padding: '0.5rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                <div style={{ fontWeight: 700, color: '#d97706', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Location</div>
                                {evt.formatted_address && <div>{evt.formatted_address}</div>}
                                {evt.latitude && evt.longitude && <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>Lat: {evt.latitude}, Lng: {evt.longitude}</div>}
                                <div>Country: <b>{evt.country_code || '-'}</b></div>
                              </div>

                              {/* Labels */}
                              {(Array.isArray(evt.phq_labels) ? evt.phq_labels : []).length > 0 && (
                                <div style={{ background: '#fff', padding: '0.5rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                  <div style={{ fontWeight: 700, color: '#059669', marginBottom: '0.25rem', fontSize: '0.75rem' }}>AI Labels</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
                                    {(Array.isArray(evt.phq_labels) ? evt.phq_labels : []).map((lbl, li) => (
                                      <span key={li} style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: 3, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>
                                        {lbl.label || lbl} {lbl.weight ? `(${(lbl.weight * 100).toFixed(0)}%)` : ''}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Impact Patterns */}
                              {(evt.impact_pattern_accommodation?.impacts || evt.impact_pattern_hospitality?.impacts) && (
                                <div style={{ background: '#fff', padding: '0.5rem', borderRadius: 6, border: '1px solid #e5e7eb', gridColumn: 'span 2' }}>
                                  <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Demand Impact Pattern</div>
                                  {evt.impact_pattern_accommodation?.impacts && (
                                    <div style={{ marginBottom: '0.3rem' }}>
                                      <div style={{ fontWeight: 600, fontSize: '0.7rem', color: '#1e40af' }}>Hotel/Accommodation demand surge:</div>
                                      <div style={{ display: 'flex', gap: '0.2rem', flexWrap: 'wrap' }}>
                                        {evt.impact_pattern_accommodation.impacts.map((imp, ii) => (
                                          <span key={ii} style={{ fontSize: '0.62rem', padding: '1px 4px', borderRadius: 3, fontWeight: 600,
                                            background: imp.position === 'event_day' ? '#fee2e2' : imp.position === 'leading' ? '#fef3c7' : '#f0fdf4',
                                            color: imp.position === 'event_day' ? '#991b1b' : imp.position === 'leading' ? '#92400e' : '#166534' }}>
                                            {imp.date_local?.slice(5)}: {imp.value > 0 ? '+' : ''}{imp.value}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {evt.impact_pattern_hospitality?.impacts && (
                                    <div>
                                      <div style={{ fontWeight: 600, fontSize: '0.7rem', color: '#9333ea' }}>F&B/Banquet demand surge:</div>
                                      <div style={{ display: 'flex', gap: '0.2rem', flexWrap: 'wrap' }}>
                                        {evt.impact_pattern_hospitality.impacts.map((imp, ii) => (
                                          <span key={ii} style={{ fontSize: '0.62rem', padding: '1px 4px', borderRadius: 3, fontWeight: 600,
                                            background: imp.position === 'event_day' ? '#fce7f3' : imp.position === 'leading' ? '#fef3c7' : '#f0fdf4',
                                            color: imp.position === 'event_day' ? '#9d174d' : imp.position === 'leading' ? '#92400e' : '#166534' }}>
                                            {imp.date_local?.slice(5)}: {imp.value > 0 ? '+' : ''}{imp.value}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add / Edit Form */}
      {showForm && (
        <div className="card card-body" style={{ marginBottom: '0.8rem', border: '2px solid var(--theme-primary)', background: '#f8fafc' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <div style={{ fontWeight: 700, color: 'var(--theme-primary)', fontSize: '0.95rem' }}>
              {editId ? 'Edit Event' : 'Add New Event'}
            </div>
            <button onClick={() => { setShowForm(false); setEditId(null); }}
              style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' }}>x</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.6rem' }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Event Name *</label>
              <input type="text" value={form.event_name} onChange={e => setForm(f => ({ ...f, event_name: e.target.value }))}
                placeholder="e.g., Diwali Festival, Tech Conference 2026" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Event Type *</label>
              <select value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))} style={inputStyle}>
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Expected Impact *</label>
              <select value={form.expected_impact} onChange={e => setForm(f => ({ ...f, expected_impact: e.target.value }))} style={inputStyle}>
                {IMPACT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label} Impact</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Start Date *</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => syncDateRange({ ...f, start_date: e.target.value }, 'start_date', 'end_date', 'start_date'))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>End Date *</label>
              <input type="date" value={form.end_date} min={form.start_date || undefined} onChange={e => setForm(f => syncDateRange({ ...f, end_date: e.target.value }, 'start_date', 'end_date', 'end_date'))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Recurring Yearly</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_recurring} onChange={e => setForm(f => ({ ...f, is_recurring: e.target.checked }))} />
                This event repeats every year
              </label>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Notes</label>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Additional details about impact on hotel demand..." style={inputStyle} />
            </div>
          </div>
          <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={saveEvent} disabled={formLoading}
              style={{ padding: '0.4rem 1.2rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', opacity: formLoading ? 0.6 : 1 }}>
              {formLoading ? 'Saving...' : (editId ? 'Update Event' : 'Add Event')}
            </button>
            <button onClick={() => { setShowForm(false); setEditId(null); }}
              style={{ padding: '0.4rem 1rem', background: '#e2e8f0', color: '#1f2937', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {viewMode === 'calendar' && renderCalendar()}
      {viewMode === 'table' && (
        <>
          {renderTable(upcomingEvents, 'Upcoming & Active Events', true)}
          {pastEvents.length > 0 && renderTable(pastEvents, 'Past Events', false)}
        </>
      )}
    </div>
  );
}

export default PmsLocalEventCalendar;
